import { isArray } from 'lodash';
import { RSAA } from 'redux-api-middleware';
import { Dispatch } from 'redux';
import { TGetRootState, TAsyncDispatchResult } from 'types/shared';
import { IRegionProps, TDispatchResult } from 'types/shared';

const GET_REQUEST = 'OPUS/CUSTOM_REGIONS/GET_REQUEST';
const PUT_REQUEST = 'OPUS/CUSTOM_REGIONS/PUT_REQUEST';
const GET_SUCCESS = 'OPUS/CUSTOM_REGIONS/GET_SUCCESS';
const PUT_SUCCESS = 'OPUS/CUSTOM_REGIONS/PUT_SUCCESS';
const FAILURE = 'OPUS/CUSTOM_REGIONS/FAILURE';

type SelectedCustomRegionsActionTypes =
  | typeof GET_REQUEST
  | typeof PUT_REQUEST
  | typeof GET_SUCCESS
  | typeof PUT_SUCCESS
  | typeof FAILURE;

interface ISelectedCustomRegionsAction {
  type: SelectedCustomRegionsActionTypes;
  error?: boolean;
  payload: any;
}
export interface ICustomRegionsState {
  pending: boolean;
  fulfilled: boolean;
  error: boolean;
  rejected: boolean;
  reason: any;
  data: [] | Record<string, IRegionProps>;
}

const initialState: ICustomRegionsState = {
  pending: false,
  fulfilled: false,
  error: false,
  rejected: false,
  reason: null,
  data: [],
};

export default function reducer(
  state = initialState,
  action: ISelectedCustomRegionsAction
): ICustomRegionsState {
  switch (action.type) {
    case GET_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
      };
    case PUT_REQUEST:
      return {
        ...state,
        pending: true,
        fulfilled: false,
        rejected: false,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case PUT_SUCCESS:
      // We do not update the on put success because we are updating optimistically
      // and the user might already have newer settings once the success comes in.
      // If the PUT call fails the user will see an error message in the DSP selector
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
      };
    case GET_SUCCESS:
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        reason: null,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: Boolean(action.error),
        reason: action.payload,
      };

    default:
      return state;
  }
}

export function saveCustomRegion(
  newRegion: any | any[],
  key?: string
): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { customRegions } = getState();
    const current = customRegions.data;

    const payload = key
      ? { ...current, [key]: { ...newRegion } }
      : { ...current, [newRegion.id]: { ...newRegion } };

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_regions`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

export function removeCustomRegion(regionId?: string): TAsyncDispatchResult {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const { customRegions } = getState();
    const current = customRegions.data;

    if (regionId && !isArray(current) && current[regionId as any]) {
      delete current[regionId as any];
    }
    const payload = current;

    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: PUT_REQUEST,
            payload: payload,
          },
          {
            type: PUT_SUCCESS,
            payload: payload,
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_regions`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      },
    });
  };
}

export function loadCustomRegions(): TAsyncDispatchResult {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    // get the user id
    const { id } = getState().authentication.profile;

    const { customRegions } = getState();

    // fetch the users' custom regions
    return await dispatch({
      [RSAA]: {
        bailout: customRegions.pending || customRegions.fulfilled,
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: async function payload(
              action: ISelectedCustomRegionsAction,
              state: ICustomRegionsState,
              res: any
            ): Promise<any> {
              const customRegions = await res.json();
              return customRegions;
            },
          },
          {
            type: FAILURE,
            meta: (_action, _state, res: any): void => {
              if (res?.status === 404) {
                loadDefaultsIf404()(dispatch, getState);
              }
            },
          },
        ],
        endpoint: `/kv/user_${id}_custom_regions`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };
}

const loadDefaultsIf404 =
  (): TAsyncDispatchResult =>
  (dispatch: Dispatch, getState: TGetRootState): any => {
    // get the user id
    const { id } = getState().authentication.profile;

    // fetch the users' custom regions
    return dispatch({
      [RSAA]: {
        types: [
          GET_REQUEST,
          {
            type: GET_SUCCESS,
            payload: {},
          },
          FAILURE,
        ],
        endpoint: `/kv/user_${id}_custom_regions`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });
  };

export const shouldLoadCustomRegions = (): TDispatchResult<boolean> => {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const state = getState();
    const { customRegions } = state;
    const { pending, data, fulfilled } = customRegions;
    return (!data && !pending) || (!pending && !fulfilled);
  };
};
